import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowLeft,
  Eye,
  Lightbulb,
  Loader2,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { useRedirectOnNotFound } from '../../lib/useRedirectOnNotFound'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import {
  SERVEXA_FEATURE_STATUSES,
  servexaFeature,
  servexaFeatureResult,
  servexaFeaturesResponse,
} from '../../lib/types'
import { descriptionSchema, subjectTextSchema } from '../../lib/validators'
import { notSentBanner, notSentNotice } from '../../lib/servexaSync'

// Shared validators — same rules the rest of the app uses. Built once at
// module load. Title reuses the support ticket's Subject schema (5–100);
// Description reuses the shared 20–1000 rule.
const TITLE_SCHEMA = subjectTextSchema()
const DESCRIPTION_SCHEMA = descriptionSchema()

// Friendly labels for the raw Servexa feature status wire values. The list +
// detail show these; the export column carries the same label so a CSV reads
// the way the screen does.
const STATUS_LABEL = {
  NEW: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Not planned',
  RELEASED: 'Released',
}

// Chip tones keyed by status — same token sets the Support list uses so the
// two pages read as one family: info-blue on submission, accent on approval,
// muted on a decline, success-green on release.
const STATUS_TONE = {
  NEW: 'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border border-[color:var(--color-info-border)]',
  APPROVED:
    'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]',
  REJECTED:
    'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
  RELEASED:
    'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
}

/** A suggestion hasn't reached Servexa yet (spec A12). */
function isNotSent(f) {
  return f.syncState === 'pending_sync' || f.syncState === 'failed'
}

/**
 * Feature Suggestions — the authenticated, tenant-scoped counterpart to the
 * Support Tickets page, backed by the Servexa Product Feedback integration
 * (spec A7). A SEPARATE flow from Support: users list their company's
 * suggestions, file a new one, read one back, and retry a send that hasn't
 * reached Servexa. No reply thread, no edit/delete — suggestions are
 * one-way ideas the product team triages.
 */
export function ServexaFeaturesPage() {
  // `/feature-suggestions/:featureId` deep-links straight onto a suggestion
  // (e.g. from a status-change notification). Seed the open id from the URL
  // param and keep it in sync if the param changes while already mounted.
  const { featureId } = useParams()
  const [statusFilter, setStatusFilter] = useState('all')
  const [openId, setOpenId] = useState(featureId ?? null)
  const [composing, setComposing] = useState(false)

  useEffect(() => {
    if (featureId) setOpenId(featureId)
  }, [featureId])

  // One unfiltered fetch; the status tabs slice CLIENT-side so the per-status
  // counts always reconcile with the rows below — mirrors the Support list.
  const featuresQuery = useQuery({
    queryKey: ['servexa-features'],
    queryFn: async () => {
      const res = await api.get('/servexa/features')
      return servexaFeaturesResponse.parse(res.data)
    },
  })

  const allRows = useMemo(
    () => featuresQuery.data?.data ?? [],
    [featuresQuery.data],
  )
  const countByStatus = useMemo(() => {
    const map = { all: allRows.length }
    for (const f of allRows) map[f.status] = (map[f.status] ?? 0) + 1
    return map
  }, [allRows])
  const rows = useMemo(
    () =>
      statusFilter === 'all'
        ? allRows
        : allRows.filter((f) => f.status === statusFilter),
    [allRows, statusFilter],
  )

  const columns = useMemo(
    () => [
      {
        key: 'featureNumber',
        header: 'Feature',
        minWidth: 110,
        sortAccessor: (f) => f.featureNumber ?? '',
        exportValue: (f) => f.featureNumber ?? '',
        render: (f) => (
          <span className="text-xs font-semibold text-[color:var(--color-ink-muted)]">
            {f.featureNumber ?? '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 130,
        sortAccessor: (f) => f.status ?? '',
        exportValue: (f) => STATUS_LABEL[f.status] ?? f.status,
        render: (f) => <StatusChip status={f.status} />,
      },
      {
        key: 'title',
        header: 'Suggestion',
        grow: true,
        pinnable: true,
        minWidth: 260,
        sortAccessor: (f) => (f.title ?? '').toLowerCase(),
        searchAccessor: (f) =>
          `${f.title ?? ''} ${f.description ?? ''} ${f.featureNumber ?? ''}`,
        exportValue: (f) => f.title,
        render: (f) => (
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
                {f.title}
              </p>
              {isNotSent(f) ? (
                <span className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]">
                  <AlertTriangle size={10} />
                  Not sent
                </span>
              ) : null}
            </div>
            <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
              {f.description}
            </p>
            {f.moduleDisabled ? (
              <p className="mt-0.5 text-xs text-[color:var(--color-warning)]">
                Feature suggestions are temporarily unavailable.
              </p>
            ) : null}
          </div>
        ),
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        minWidth: 160,
        sortAccessor: (f) => (f.updatedAt ? new Date(f.updatedAt) : null),
        exportValue: (f) => formatDateTime(f.updatedAt),
        render: (f) => (
          <span className="text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap">
            {formatDateTime(f.updatedAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        // Singular: this column renders exactly ONE row action, matching the
        // Support list's "Action" header for the same one-item menu.
        header: 'Action',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (f) => (
          <RowActionsMenu
            label={`Actions for ${f.title}`}
            items={[
              {
                key: 'view',
                label: 'View suggestion',
                icon: Eye,
                onClick: () => setOpenId(f.id),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  if (openId) {
    return <FeatureDetail featureId={openId} onBack={() => setOpenId(null)} />
  }

  return (
    <div>
      <Breadcrumb current="Feature Suggestions" />
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Lightbulb size={20} className="text-[color:var(--color-accent-strong)]" />
            Feature Suggestions
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Share ideas to shape TaskMagix and track where each one stands.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setComposing(true)}>
          <Plus size={14} />
          New Suggestion
        </Button>
      </div>

      {/* Status pills — client-side slice with per-status counts, mirroring
       *  the Support list's tabs so the two pages behave identically. */}
      <div className="flex items-center gap-1 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5 w-fit mb-4">
        {['all', ...SERVEXA_FEATURE_STATUSES].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={
              'px-3 py-1.5 rounded text-xs font-medium cursor-pointer ' +
              (statusFilter === s
                ? 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-strong)] shadow-sm'
                : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
            }
          >
            {`${s === 'all' ? 'All' : STATUS_LABEL[s]} (${countByStatus[s] ?? 0})`}
          </button>
        ))}
      </div>

      {featuresQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
      ) : featuresQuery.isError ? (
        // Surface the real error rather than falling into the empty state —
        // an empty list should never mask a 403/500 (Support-list lesson).
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(featuresQuery.error).message}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          statusFilter={statusFilter}
          onCreate={() => setComposing(true)}
          onClearFilter={() => setStatusFilter('all')}
        />
      ) : (
        <DataTable
          tableId="app-servexa-features"
          columns={columns}
          rows={rows}
          getRowId={(f) => f.id}
          title="Feature Suggestions"
          exportBaseName="feature-suggestions"
          exportTitle="Feature Suggestions"
          selectable
          searchPlaceholder="Search by title or feature ID"
          defaultSort={[{ key: 'updatedAt', dir: 'desc' }]}
          emptyState={{
            icon: Lightbulb,
            title: 'No suggestions match your search',
            body: 'Try a different search term, or clear it to see all suggestions in this status.',
          }}
        />
      )}

      {composing ? <CreateDialog onClose={() => setComposing(false)} /> : null}
    </div>
  )
}

/** Feature status chip for the list Status column + the detail header. */
function StatusChip({ status }) {
  return (
    <span
      className={
        'text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded w-fit inline-block ' +
        (STATUS_TONE[status] ?? '')
      }
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}

function EmptyState({ statusFilter, onCreate, onClearFilter }) {
  // A specific status tab with no matches — suggestions may live under other
  // tabs, so DON'T show the first-time prompt. Context-aware copy + a link
  // back to the full list instead.
  if (statusFilter && statusFilter !== 'all') {
    return (
      <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
        <Lightbulb size={28} className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]" />
        <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
          No {(STATUS_LABEL[statusFilter] ?? '').toLowerCase()} suggestions
        </p>
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] max-w-md mx-auto">
          No suggestions have this status right now. Check another tab to see
          the rest of your ideas.
        </p>
        <button
          type="button"
          onClick={onClearFilter}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
        >
          Show All Suggestions
        </button>
      </div>
    )
  }
  // Genuine first-time empty — the All tab with no suggestions at all.
  return (
    <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
      <Lightbulb size={28} className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]" />
      <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
        No feature suggestions yet
      </p>
      <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] max-w-md mx-auto">
        Got an idea that would make TaskMagix better? Share it with our team —
        we review every suggestion and update its status here.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer"
      >
        <Plus size={14} />
        Suggest a Feature
      </button>
    </div>
  )
}

function FeatureDetail({ featureId, onBack }) {
  const qc = useQueryClient()

  const detailQuery = useQuery({
    queryKey: ['servexa-feature', featureId],
    queryFn: async () => {
      const res = await api.get(`/servexa/features/${featureId}`)
      return servexaFeature.parse(res.data)
    },
  })

  // Don't strand the viewer on a dead "not found" page when the deep-link is
  // stale or inaccessible — bounce back to the list with a notice.
  useRedirectOnNotFound(
    detailQuery,
    onBack,
    'This suggestion is no longer available.',
  )

  // Retry a send that hasn't reached Servexa (spec A12). Refresh BOTH this
  // detail and the list so the "Not sent" badge / feature number reconcile.
  const retryMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/servexa/features/${featureId}/retry`)
      return servexaFeatureResult.parse(res.data)
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['servexa-feature', featureId] })
      void qc.invalidateQueries({ queryKey: ['servexa-features'] })
      if (result.submitted) {
        toast.success(
          result.featureNumber
            ? `Suggestion sent — reference ${result.featureNumber}.`
            : 'Suggestion sent to our team.',
        )
      } else if (result.moduleDisabled) {
        toast.message(
          'Feature suggestions are currently unavailable — please try again later.',
        )
      } else {
        // Queued vs could-not-be-sent read differently (spec A12/A22).
        const notice = notSentNotice(result, 'Suggestion')
        ;(notice.tone === 'warning' ? toast.warning : toast.message)(notice.text)
      }
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  if (detailQuery.isLoading) {
    return (
      <div>
        <Breadcrumb current="Feature Suggestions" />
        <div className="grid place-items-center py-24">
          <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading suggestion…
          </p>
        </div>
      </div>
    )
  }

  const f = detailQuery.data
  if (!f) {
    return (
      <div>
        <Breadcrumb current="Feature Suggestions" />
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back to Suggestions
        </button>
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {detailQuery.isError
            ? extractApiError(detailQuery.error).message
            : 'Suggestion not found.'}
        </p>
      </div>
    )
  }

  const notSent = isNotSent(f)

  return (
    <div>
      <Breadcrumb current="Feature Suggestions" />
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={14} /> Back to Suggestions
      </button>

      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-2xl text-[color:var(--color-ink-strong)] break-words [overflow-wrap:anywhere]">
              {f.title}
            </h1>
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              {f.featureNumber ?? 'Pending'}
              {f.createdAt ? ` · Suggested ${formatDateTime(f.createdAt)}` : ''}
              {f.updatedAt ? ` · Last update ${formatDateTime(f.updatedAt)}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusChip status={f.status} />
          </div>
        </div>

        {/* Honest module note (spec A7) — shown standalone only when the
         *  "Not sent" callout below isn't already carrying the message. */}
        {f.moduleDisabled && !notSent ? (
          <p className="mt-3 text-xs text-[color:var(--color-warning)]">
            Feature suggestions are temporarily unavailable.
          </p>
        ) : null}

        <p className="mt-4 text-sm text-[color:var(--color-ink)] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
          {f.description}
        </p>

        {/* Business need — optional; omit the whole block when null. */}
        {f.businessNeed ? (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1.5">
              Business need
            </p>
            <p className="text-sm text-[color:var(--color-ink)] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
              {f.businessNeed}
            </p>
          </div>
        ) : null}

        {/* Not-sent callout (spec A12) — the suggestion is saved locally but
         *  hasn't reached Servexa. Warn, surface the last sync error, and
         *  offer a manual retry. Only rendered while unsent; a synced
         *  suggestion has no retry. */}
        {notSent ? (
          <div className="mt-4 rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle
                size={16}
                className="shrink-0 mt-0.5 text-[color:var(--color-warning)]"
              />
              <div className="min-w-0 flex-1">
                {/* Queued (sent automatically) vs could-not-be-sent (only Retry
                 *  resends it) — the old copy promised automatic retries for
                 *  both, including a switched-off module that is never retried. */}
                <p className="text-sm font-semibold text-[color:var(--color-warning)]">
                  {notSentBanner(f).title}
                </p>
                <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] break-words [overflow-wrap:anywhere]">
                  {notSentBanner(f).body}
                </p>
                <div className="mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => retryMutation.mutate()}
                    loading={retryMutation.isPending}
                  >
                    <RefreshCw size={14} />
                    {retryMutation.isPending ? 'Retrying…' : 'Retry Send'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Read-only reassurance — this flow has no reply thread; the status
         *  chip above is how the team communicates back. */}
        <p className="mt-4 pt-4 border-t border-[color:var(--color-rule)] text-xs text-[color:var(--color-ink-muted)] italic">
          Our team reviews suggestions and updates the status here.
        </p>
      </div>
    </div>
  )
}

function CreateDialog({ onClose }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    title: '',
    description: '',
    businessNeed: '',
  })
  const [errors, setErrors] = useState({})

  // Pin a single source of truth for the field rules so the live validation
  // and the submit gate stay in sync with the shared validators.
  function validateField(field, value) {
    if (field !== 'title' && field !== 'description') return undefined
    const schema = field === 'title' ? TITLE_SCHEMA : DESCRIPTION_SCHEMA
    const r = schema.safeParse(value)
    return r.success ? undefined : r.error.issues[0]?.message
  }
  function setField(field, value) {
    // Strip leading whitespace as the user types (all fields are free text);
    // trailing whitespace is reaped on blur and again at schema-parse time.
    const next = typeof value === 'string' ? value.replace(/^\s+/, '') : value
    setForm((f) => ({ ...f, [field]: next }))
    // Re-validate live once an error is already showing; also surface a
    // MAX-length error the instant the cap is exceeded. Minimum-length errors
    // stay deferred to blur/submit so we don't nag mid-typing.
    if (field === 'title' || field === 'description') {
      const msg = validateField(field, next)
      if (errors[field] || (msg && /at most/.test(msg))) {
        setErrors((e) => ({ ...e, [field]: msg }))
      }
    }
  }
  function trimFieldOnBlur(field) {
    setForm((f) => ({
      ...f,
      [field]:
        typeof f[field] === 'string' ? f[field].replace(/\s+$/, '') : f[field],
    }))
  }
  function validateAll() {
    const next = {
      title: validateField('title', form.title),
      description: validateField('description', form.description),
    }
    setErrors(next)
    return !next.title && !next.description
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!validateAll()) {
        throw new Error('validation_failed')
      }
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
      }
      // Send `businessNeed` only when the optional field carries something.
      const bn = form.businessNeed.trim()
      if (bn) body.businessNeed = bn
      const res = await api.post('/servexa/features', body)
      return servexaFeatureResult.parse(res.data)
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['servexa-features'] })
      if (result.submitted) {
        toast.success(
          result.featureNumber
            ? `Suggestion submitted — reference ${result.featureNumber}.`
            : 'Suggestion submitted to our team.',
        )
      } else if (result.moduleDisabled) {
        toast.message(
          'Feature suggestions are currently unavailable — please try again later.',
        )
      } else {
        // Saved but not sent: say whether it is queued or needs a retry.
        const notice = notSentNotice(result, 'Suggestion')
        ;(notice.tone === 'warning' ? toast.warning : toast.message)(notice.text)
      }
      onClose()
    },
    onError: (err) => {
      if (err?.message === 'validation_failed') return
      toast.error(extractApiError(err).message)
    },
  })

  // Dismissal matches the shared Modal (and the Support ComposeDialog):
  // Escape / X / Cancel close; a backdrop click deliberately does NOT, so a
  // stray click never discards a half-typed suggestion. Guarded while the
  // create is in flight.
  const dismiss = () => (createMutation.isPending ? undefined : onClose())

  const inputBase =
    'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] outline-none focus:border-[color:var(--color-accent)] '
  const areaBase =
    'w-full px-3 py-2 rounded-md border bg-[color:var(--color-canvas)] outline-none focus:border-[color:var(--color-accent)] resize-y text-sm '
  const borderFor = (err) =>
    err
      ? 'border-[color:var(--color-priority-urgent)]'
      : 'border-[color:var(--color-rule)]'

  return (
    <Modal
      open
      onClose={dismiss}
      title="Suggest a feature"
      description="Tell us what you'd like TaskMagix to do. Our product team reviews every suggestion."
      size="ml"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          {/* Kept enabled on empty fields (only disabled mid-submit) so a
           *  click runs validateAll and surfaces the required-field errors
           *  instead of a dead, greyed-out button. */}
          <Button
            onClick={() => createMutation.mutate()}
            loading={createMutation.isPending}
          >
            {createMutation.isPending ? 'Submitting…' : 'Submit Suggestion'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Title
            <span className="text-[color:var(--color-priority-urgent)] ml-0.5">*</span>
          </label>
          <input
            value={form.title}
            onChange={(e) => setField('title', e.target.value)}
            onBlur={() => {
              trimFieldOnBlur('title')
              setErrors((e) => ({
                ...e,
                title: validateField('title', form.title.replace(/\s+$/, '')),
              }))
            }}
            placeholder="Enter a short, clear title"
            className={inputBase + borderFor(errors.title)}
          />
          {errors.title ? (
            <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {errors.title}
            </p>
          ) : null}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Description
            <span className="text-[color:var(--color-priority-urgent)] ml-0.5">*</span>
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            onBlur={() => {
              trimFieldOnBlur('description')
              setErrors((e) => ({
                ...e,
                description: validateField(
                  'description',
                  form.description.replace(/\s+$/, ''),
                ),
              }))
            }}
            rows={5}
            placeholder="Describe the feature and how it would help"
            className={areaBase + borderFor(errors.description)}
          />
          {errors.description ? (
            <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {errors.description}
            </p>
          ) : null}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
            Business need
            <span className="ml-1 font-normal normal-case tracking-normal text-[color:var(--color-ink-muted)]">
              (optional)
            </span>
          </label>
          <textarea
            value={form.businessNeed}
            onChange={(e) => setField('businessNeed', e.target.value)}
            onBlur={() => trimFieldOnBlur('businessNeed')}
            rows={3}
            placeholder="What problem would this solve for your team?"
            className={areaBase + 'border-[color:var(--color-rule)]'}
          />
        </div>
      </div>
    </Modal>
  )
}
